// Comments Service for Blog Posts
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';

const BLOG_COLLECTION = 'blog_posts';
const COMMENTS_SUBCOLLECTION = 'comments';

/**
 * Add a comment to a blog post
 */
export const addCommentToBlogPost = async (postId, commentData) => {
  try {
    // Validate comment data
    if (!commentData.content || commentData.content.trim().length === 0) {
      return { success: false, error: 'Comment cannot be empty' };
    }

    if (commentData.content.length > 1000) {
      return { success: false, error: 'Comment is too long (max 1000 characters)' };
    }

    const postRef = doc(db, BLOG_COLLECTION, postId);
    const commentsRef = collection(postRef, COMMENTS_SUBCOLLECTION);

    const newComment = {
      ...commentData,
      createdAt: Timestamp.now(),
      approved: false, // Comments need admin approval
    };

    const docRef = await addDoc(commentsRef, newComment);

    return { success: true, id: docRef.id, data: newComment };
  } catch (error) {
    console.error('Error adding comment:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all approved comments for a blog post
 */
export const getPostComments = async (postId) => {
  try {
    console.log('Attempting to load comments for post:', postId);
    const postRef = doc(db, BLOG_COLLECTION, postId);
    const commentsRef = collection(postRef, COMMENTS_SUBCOLLECTION);

    console.log('Comments ref path:', commentsRef.path);
    
    const querySnapshot = await getDocs(commentsRef);
    console.log('Successfully loaded comments, count:', querySnapshot.docs.length);
    
    const allComments = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Filter approved comments on client side
    const approvedComments = allComments.filter(comment => comment.approved === true);
    
    console.log('Approved comments count:', approvedComments.length);
    
    // Sort by newest first
    approvedComments.sort((a, b) => (b.createdAt?.toDate?.() || b.createdAt) - (a.createdAt?.toDate?.() || a.createdAt));

    return { success: true, data: approvedComments };
  } catch (error) {
    console.error('Error getting comments:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    return { success: false, error: error.message };
  }
};

/**
 * Get pending comments (Admin only)
 */
export const getPendingComments = async () => {
  try {
    const allPosts = await getDocs(collection(db, BLOG_COLLECTION));
    const allComments = [];

    for (const postDoc of allPosts.docs) {
      const commentsRef = collection(postDoc.ref, COMMENTS_SUBCOLLECTION);
      const q = query(commentsRef, where('approved', '==', false));
      const querySnapshot = await getDocs(q);

      querySnapshot.docs.forEach(doc => {
        allComments.push({
          id: doc.id,
          postId: postDoc.id,
          postTitle: postDoc.data().title,
          ...doc.data(),
        });
      });
    }

    // Sort by newest first
    allComments.sort((a, b) => b.createdAt - a.createdAt);

    return { success: true, data: allComments };
  } catch (error) {
    console.error('Error getting pending comments:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Approve a comment (Admin only)
 */
export const approveComment = async (postId, commentId) => {
  try {
    const postRef = doc(db, BLOG_COLLECTION, postId);
    const commentRef = doc(collection(postRef, COMMENTS_SUBCOLLECTION), commentId);

    await updateDoc(commentRef, {
      approved: true,
      approvedAt: Timestamp.now(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error approving comment:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a comment
 */
export const deleteComment = async (postId, commentId) => {
  try {
    const postRef = doc(db, BLOG_COLLECTION, postId);
    const commentRef = doc(collection(postRef, COMMENTS_SUBCOLLECTION), commentId);

    await deleteDoc(commentRef);

    return { success: true };
  } catch (error) {
    console.error('Error deleting comment:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get comment count for a post
 */
export const getCommentCount = async (postId) => {
  try {
    const postRef = doc(db, BLOG_COLLECTION, postId);
    const commentsRef = collection(postRef, COMMENTS_SUBCOLLECTION);

    const q = query(commentsRef, where('approved', '==', true));
    const querySnapshot = await getDocs(q);

    return { success: true, count: querySnapshot.size };
  } catch (error) {
    console.error('Error getting comment count:', error);
    return { success: false, error: error.message };
  }
};

export default {
  addCommentToBlogPost,
  getPostComments,
  getPendingComments,
  approveComment,
  deleteComment,
  getCommentCount,
};
